import PropTypes from 'prop-types';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import normalizeProjectStoreNavList from '@Hub/Header/utils/normalizeProjectStoreNavList';

import { AppMetaContext, EnterpriseParentContext, ProjectContext } from '@core/context';
import useClassy from '@core/hooks/useClassy';
import useMediaQuery from '@core/hooks/useMediaQuery';
import { useProjectStore, useSuperHubStore } from '@core/store';

import LandingHeader from '@routes/Landing/Header';
import GitVersionDropdown from '@routes/SuperHub/components/VersionDropdown';

import classes from '../../style.module.scss';
import JumpToButton from '../JumpToButton';
import LinkMenu from '../LinkMenu';
import LogoLink from '../LogoLink';
import MobileSubnav from '../mobile/Subnav';
import NavLinks from '../NavLinks';
import ProjectPicker from '../ProjectPicker';
import Search from '../Search';
import StagingBanner from '../StagingBanner';
import ThemeOverride from '../ThemeOverride';
import TranslationDropdown from '../TranslationDropdown';
import UserMenu from '../UserMenu';
import VersionDropdown from '../VersionDropdown';

const AppSubnavItems = ({ baseUrl, lang, langFull, version }) => {
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const ent = useContext(EnterpriseParentContext);
  const showDetails = !ent.isParent || ent.singleParent;
  const [tabLeft, setTabLeft] = useState(0);
  const [tabWidth, setTabWidth] = useState(0);
  const tabsRef = useRef(null);
  const bem = useClassy(classes, 'Header');

  const { project } = useContext(ProjectContext);
  const isEnterpriseChild = project.parent;
  const [headerType, navLinkStyle, projectSubheaderLayout, pageIconVisibility, versionVisibility] = useProjectStore(
    s => [
      s.data.appearance.header.type,
      s.data.appearance.header.link_style,
      s.data.appearance.navigation.subheader_layout,
      s.data.appearance.navigation.page_icons,
      s.data.appearance.navigation.version,
    ],
  );
  const subheaderLayout = isEnterpriseChild ? project.parent.appearance.subheaderStyle : projectSubheaderLayout;

  const setActiveTabPosition = target => {
    setTabLeft(target.offsetLeft);
    setTabWidth(target.clientWidth);
  };

  const { pathname } = useLocation();

  useEffect(() => {
    const activeTab = tabsRef.current?.querySelector('.active');
    if (activeTab) {
      requestAnimationFrame(() => {
        setActiveTabPosition(activeTab);
      });
    }
  }, [headerType, navLinkStyle, pathname, pageIconVisibility, versionVisibility]);

  return (
    <nav ref={tabsRef} aria-label="Primary navigation" className={bem('-left', '-subnav')} role="navigation">
      <ProjectPicker baseUrl={baseUrl} lang={lang} />
      {!!showDetails && [
        <TranslationDropdown key="AppSubNav--translations" lang={lang} langFull={langFull} version={version} />,
        isSuperHub ? (
          <GitVersionDropdown key="AppSubNav--versions" />
        ) : (
          <VersionDropdown key="AppSubNav--versions" lang={lang} version={version} />
        ),
      ]}
      {!!showDetails && <NavLinks onClick={e => setActiveTabPosition(e)} type="base" />}
      <NavLinks onClick={e => setActiveTabPosition(e.currentTarget)} type="extra" />
      {subheaderLayout === 'links' && (
        <div
          className={bem('-subnav-tab', tabLeft && tabWidth && '-subnav-tab_active')}
          style={{ transform: `translateX(${tabLeft}px)`, width: tabWidth }}
        />
      )}
    </nav>
  );
};

const AppSubnav = ({ isDetachedProductionSite, setTheme, theme, toggleSearch, ...props }) => {
  const ent = useContext(EnterpriseParentContext);
  const appMeta = useContext(AppMetaContext);
  const enterprise = useContext(EnterpriseParentContext);
  const { project } = useContext(ProjectContext);
  const { parent } = project;
  const { pathname, search } = useLocation();
  const bem = useClassy(classes, 'Header');
  const isDesktop = useMediaQuery('(min-width: 769px)');

  const isLP = appMeta.type === 'home' || pathname === '/';

  const owlbotTrial = new URLSearchParams(search).get('owlbotTrial');
  const owlbotEnabled = project.owlbot.enabled || owlbotTrial === 'true';

  const [topNavRight, colorScheme, projectLayout, isFullWidth] = useProjectStore(s => [
    normalizeProjectStoreNavList(s.data.appearance.navigation.right),
    s.data.appearance.brand.theme,
    s.data.appearance.layout.style,
    s.data.appearance.layout.full_width === 'enabled',
  ]);

  const projectTheme = parent ? parent.appearance.colorScheme : colorScheme;
  const topRightItems = isLP && project.flags.singleProjectEnterprise ? project.parent.topnav.right : topNavRight;

  return (
    <div
      className={bem(
        '-bottom',
        `-bottom_${projectLayout}`,
        'rm-Header-bottom',
        ent.isParent && isLP && ('rm-Header-bottom_withProjects', '-bottom_withProjects'),
        owlbotEnabled && ('rm-Header-bottom_withOwlbot', '-bottom_withOwlbot'),
      )}
    >
      {projectLayout === 'compact' && <StagingBanner isDetachedProductionSite={isDetachedProductionSite} />}
      <div className={bem('rm-Container', 'rm-Container_flex', isFullWidth && 'rm-ContainerFull')}>
        {projectLayout === 'compact' && (
          <>
            <JumpToButton />
            <div className={bem('rm-Header-left', 'rm-Header-left_logo', '-left', '-left_compact')}>
              <LogoLink />
            </div>
          </>
        )}
        <AppSubnavItems {...props} />
        <MobileSubnav />
        {/* Show subnav search for all themes on mobile non-modern themes on desktop */}
        {(projectLayout !== 'modern' || (projectLayout === 'modern' && !isDesktop)) && (
          <Search
            childSubdomain={project.parent ? project.subdomain : undefined}
            toggleSearch={toggleSearch}
            {...props}
          />
        )}
        {projectLayout === 'compact' && (
          <div className={bem('rm-Header-right', '-right', '-right_desktop')}>
            {topRightItems.some(item => item.type === 'user') && <UserMenu />}
            {projectTheme === 'system' && <ThemeOverride setTheme={setTheme} />}
            <LinkMenu />
          </div>
        )}
      </div>
      {projectLayout !== 'classic' && !!isLP && !enterprise.isParent && <LandingHeader toggleSearch={toggleSearch} />}
    </div>
  );
};

AppSubnav.propTypes = {
  isDetachedProductionSite: PropTypes.bool.isRequired,
  isModalOpen: PropTypes.bool,
  lang: PropTypes.string,
  search: PropTypes.object,
  setTheme: PropTypes.func,
  theme: PropTypes.oneOf(['auto', 'dark', 'light']),
  toggleSearch: PropTypes.func,
  version: PropTypes.object,
};

AppSubnavItems.propTypes = {
  baseUrl: PropTypes.string,
  headerType: PropTypes.string,
  lang: PropTypes.string,
  langFull: PropTypes.string,
  version: PropTypes.object,
};

export default AppSubnav;
